import { useFormik } from "formik";
import React, { ChangeEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Button,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap";
import * as Yup from "yup";

// @ts-ignore
import "react-toastify/dist/ReactToastify.css";

import {
  ActivityButton,
  SequenceNumberInput,
} from "../../../components/Common";
import { CreateSubjectNoteRequest, ParsedError } from "../../../interfaces";

import { MediaDto } from "../../../interfaces/media";
import { PDF_ACCEPT } from "../../../constants";
import { applyServerFormErrors, formatFileSize } from "../../../services";
import {
  CategoryTypeEnum,
  ErrorHandlingTypeEnum,
  SequenceEnum,
} from "../../../enums";
import { CategorySelect } from "../../../components/Selects/CategorySelect";
import {
  useGetSubjectNoteQuery,
  useAddSubjectNoteMutation,
  useUpdateSubjectNoteMutation,
  useUploadSubjectNoteMutation,
  useDownloadSubjectNoteMutation,
} from "../../../store/api/subjectNoteApi";
import { toast } from "react-toastify";

type TProps = {
  id?: string | null;
  subjectId: string;
  show: boolean;
  onCloseClick: (event: any) => void;
};
export const AddEditSubjectNoteModal: React.FC<TProps> = ({
  id,
  subjectId,
  show,
  onCloseClick,
}) => {
  const { t } = useTranslation();
  const [request] = useState<CreateSubjectNoteRequest>();
  const [uploadResponse, setUploadResponse] = useState<MediaDto | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const {
    data: subjectNote,
    error: errorSubjectNote,
    isLoading: isLoadingSubjectNote,
    isError: isErrorSubjectNote,
  } = useGetSubjectNoteQuery(id as string, { skip: !id });

  const [
    addSubjectNote,
    { isLoading: isLoadingAdd, error: errorAdd, isError: isErrorAdd },
  ] = useAddSubjectNoteMutation();

  const [
    updateSubjectNote,
    { isLoading: isLoadingUpdate, error: errorUpdate, isError: isErrorUpdate },
  ] = useUpdateSubjectNoteMutation();

  const [
    uploadSubjectNote,
    { isLoading: isLoadingUpload, isError: isErrorUpload, error: errorUpload },
  ] = useUploadSubjectNoteMutation();

  const [downloadSubjectNote, { isLoading: isLoadingDownload }] =
    useDownloadSubjectNoteMutation();

  const uploadSubjectNoteResponse = uploadResponse;

  //#region form
  const form = useFormik<CreateSubjectNoteRequest>({
    enableReinitialize: true,
    initialValues: {
      name: {
        en: request?.name.en || "",
        ar: request?.name.ar || "",
      },
      fileId: "",
      sequenceNumber: request?.sequenceNumber || 0,
      subjectId: subjectId,
      categoryId: request?.categoryId || "",
    },
    validationSchema: Yup.object({
      name: Yup.object({
        en: Yup.string()
          .max(
            100,
            t("maxLength", { maxLength: 100, displayName: "englishValue" }),
          )
          .required(
            t("isNotEmpty", {
              displayName: "englishValue",
            }),
          ),
        ar: Yup.string()
          .max(
            100,
            t("maxLength", { maxLength: 100, displayName: "englishValue" }),
          )
          .required(
            t("isNotEmpty", {
              displayName: "arabicValue",
            }),
          ),
      }),
      sequenceNumber: Yup.number().required(
        t("isNotEmpty", {
          displayName: "sequenceNumber",
        }),
      ),
      categoryId: Yup.string().required(
        t("isNotEmpty", {
          displayName: "category",
        }),
      ),
    }),
    onSubmit: async (values) => {
      try {
        if (id) {
          await updateSubjectNote({
            id,
            data: { ...values, sequenceNumber: Number(values.sequenceNumber) },
          }).unwrap();
        } else {
          await addSubjectNote({
            ...values,
            sequenceNumber: Number(values.sequenceNumber),
          }).unwrap();
        }
        onClose();
      } catch (err) {
        console.error(err);
      }
    },
  });
  //#endregion

  //#region effects
  useEffect(() => {
    if (id && subjectNote) {
      form.setValues({
        name: subjectNote.name,
        fileId: subjectNote?.file?.id || "",
        sequenceNumber:
          form.values.sequenceNumber || subjectNote?.sequenceNumber || 0,
        subjectId: subjectId,
        categoryId: subjectNote?.category?.id || "",
      });
    }
  }, [subjectNote, isLoadingSubjectNote, id]);

  useEffect(() => {
    if (!id) {
      setUploadResponse(null);
      setUploadError(null);
      form.resetForm({
        values: {
          name: { en: "", ar: "" },
          fileId: "",
          sequenceNumber: 0,
          subjectId: subjectId,
          categoryId: "",
        },
      });
    }
  }, [id, subjectId]);

  useEffect(() => {
    let _error: ParsedError | null = null;
    if (!isLoadingAdd && isErrorAdd) _error = errorAdd as ParsedError;
    if (!isLoadingUpdate && isErrorUpdate) _error = errorUpdate as ParsedError;
    if (!isLoadingUpload && isErrorUpload) _error = errorUpload as ParsedError;
    if (_error?.type == ErrorHandlingTypeEnum.FORM) {
      applyServerFormErrors(form, _error.error);
      if (typeof _error.error?.fileId === "string") {
        setUploadError(_error.error.fileId);
      }
    }
  }, [isLoadingAdd, isLoadingUpdate, isLoadingUpload]);

  //#endregion

  //#region handlers
  const onClose = () => {
    form.resetForm();
    setUploadResponse(null);
    setUploadError(null);
    onCloseClick(!show);
  };

  const onChangeFile = (event: ChangeEvent<HTMLInputElement>) => {
    const { target } = event;
    const { files } = target;
    if (files && files.length > 0) {
      const pdfFile = files[0];
      if (!pdfFile.name.match(/\.(pdf)$/)) {
        toast.error(t("selectPdfOnly"), {
          autoClose: 3000,
        });
        return false;
      }
      setUploadError(null);
      uploadSubjectNote({
        file: pdfFile,
        subjectNoteId: id ?? undefined,
      })
        .unwrap()
        .then((res) => {
          setUploadResponse(res);
          form.setFieldValue("fileId", res.id);
        })
        .catch((err) => {
          const msg =
            (err as any)?.data?.message ||
            t("uploadError") ||
            "An error occurred";
          setUploadError(msg);
          toast.error(msg, { autoClose: 3000 });
        });
      event.target.value = "";
    }
  };

  const onDownload = async () => {
    if (!id) return;
    try {
      const blob = await downloadSubjectNote(id).unwrap();
      const blobUrl = window.URL.createObjectURL(
        new Blob([blob], { type: "application/pdf" }),
      );
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute(
        "download",
        subjectNote?.file?.name ||
          uploadSubjectNoteResponse?.name ||
          "downloaded.pdf",
      );
      document.body.appendChild(link);
      link.click();
      if (link.parentNode) link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      toast.error(t("downloadingError"), { autoClose: 3000 });
    }
  };

  //#endregion

  return (
    <React.Fragment>
      <Modal
        id="addEdit"
        isOpen={show}
        toggle={() => {
          onClose();
        }}
        centered
        size="xl"
      >
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
            return false;
          }}
          action="#"
        >
          <ModalHeader
            className="p-3"
            toggle={() => {
              onClose();
            }}
          >
            {id
              ? t("editItem", { item: "subjectNote" })
              : t("addItem", { item: "subjectNote" })}
          </ModalHeader>
          <ModalBody>
            <div
              className="text-center p-4 mb-3"
              style={{ border: "solid 1px #ced4da", borderRadius: "5px" }}
            >
              <label htmlFor="subject-note-input" className="cursor-pointer">
                {id && subjectNote ? (
                  <i className="ri-file-pdf-line align-bottom text-danger display-5 cursor-pointer"></i>
                ) : (
                  <i className="ri-file-upload-line align-bottom text-danger display-5 cursor-pointer"></i>
                )}
                {!uploadSubjectNoteResponse && (!id || !subjectNote?.file) && (
                  <h6 className="fs-12 folder-name mt-1 cursor-pointer">
                    {t("clickToUpload")}
                  </h6>
                )}
                <h6 className="fs-12 text-mute cursor-pointer">
                  {(typeof uploadError === "string" || form.errors.fileId) && (
                    <Label className="invalid-feedback d-block">
                      {uploadError || (form.errors.fileId as string)}
                    </Label>
                  )}
                </h6>
                <div className=" text-muted">
                  {((id && subjectNote?.file) ||
                    uploadSubjectNoteResponse?.path) && (
                    <React.Fragment>
                      <span className="me-auto text-center">
                        <b>{t("name")}</b>{" "}
                        {uploadSubjectNoteResponse?.name ||
                          subjectNote?.file?.name}
                      </span>
                      <br />
                      <span className="me-auto">
                        <b>{t("size")}</b>{" "}
                        {formatFileSize(
                          Number(uploadSubjectNoteResponse?.size) ||
                            (id && subjectNote?.file
                              ? Number(subjectNote.file.size)
                              : 0) ||
                            0,
                        )}
                      </span>
                    </React.Fragment>
                  )}
                  <br />
                  <span>{isLoadingUpload && <Spinner size={"sm"} />}</span>
                </div>
              </label>
              <br />
              {((id && subjectNote?.file?.path) ||
                uploadSubjectNoteResponse?.path) &&
                !isLoadingUpload && (
                  <div>
                    {isLoadingDownload ? (
                      <span className="me-auto">
                        <Spinner size={"sm"} /> {t("downloading")}
                      </span>
                    ) : (
                      <Button
                        onClick={onDownload}
                        className="btn btn-sm btn-primary"
                      >
                        {t("download")}
                      </Button>
                    )}
                  </div>
                )}
              <Input
                id="subject-note-input"
                type="file"
                className="profile-foreground-img-file-input d-none"
                accept={PDF_ACCEPT}
                onChange={onChangeFile}
                disabled={isLoadingUpload}
              />
            </div>
            <SequenceNumberInput
              tabIndex={1}
              value={form.values.sequenceNumber}
              onChange={(e) => {
                e.preventDefault();
                const { value } = e.target;
                const regex =
                  /^(0*[0-9][0-9]*(\.[0-9]*)?|0*\.[0-9]*[1-9][0-9]*)$/;
                if (regex.test(value.toString())) {
                  form.setFieldValue("sequenceNumber", value);
                } else {
                  form.setFieldValue("sequenceNumber", "");
                }
              }}
              onBlur={form.handleBlur}
              invalid={
                form.touched.sequenceNumber && form.errors.sequenceNumber
                  ? true
                  : false
              }
              sequenceType={SequenceEnum.SUBJECT_NOTE}
              onGenerateSequenceNumber={(v: number) => {
                form.setFieldValue("sequenceNumber", v);
              }}
              mode={id ? "edit" : "add"}
              request={{
                type: SequenceEnum.SUBJECT_NOTE,
                subjectId: subjectId,
              }}
            />
            {form.touched.sequenceNumber && form.errors.sequenceNumber ? (
              <FormFeedback type="invalid">
                {form.errors.sequenceNumber as string}
              </FormFeedback>
            ) : null}

            <div className="mb-3">
              <Label htmlFor="categoryId" className="form-label">
                {t("category")}
                <span className="text-danger">*</span>
              </Label>
              <CategorySelect
                type="select"
                categoryType={CategoryTypeEnum.SUBJECT_NOTE}
                name="categoryId"
                className="form-select"
                aria-label={t("selectCategory")}
                placeholder={t("selectCategory")}
                value={form.values.categoryId}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                invalid={
                  form.touched.categoryId && form.errors.categoryId
                    ? true
                    : false
                }
              ></CategorySelect>
              {form.touched.categoryId && form.errors.categoryId ? (
                <FormFeedback type="invalid">
                  {form.errors.categoryId}
                </FormFeedback>
              ) : null}
            </div>
            <div className="mb-3">
              <label htmlFor="name.en" className="form-label">
                {t("nameEn")}
                <span className="text-danger">*</span>
              </label>
              <Input
                disabled={!!errorSubjectNote}
                autoComplete="off"
                id="name.en"
                name="name.en"
                value={form.values?.name.en}
                type="text"
                className="form-control"
                placeholder={t("enterInEnglish")}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                invalid={
                  form.touched.name?.en && form.errors.name?.en ? true : false
                }
              />
              {form.touched.name?.en && form.errors.name?.en ? (
                <FormFeedback type="invalid">
                  {form.errors.name?.en as string}
                </FormFeedback>
              ) : null}
            </div>
            <div className="mb-3">
              <label htmlFor="name.ar" className="form-label">
                {t("nameAr")}
                <span className="text-danger">*</span>
              </label>
              <Input
                style={{ fontFamily: "aljannat", direction: "rtl" }}
                disabled={!!errorSubjectNote}
                autoComplete="off"
                id="name.ar"
                name="name.ar"
                value={form.values.name?.ar}
                type="text"
                className="form-control"
                placeholder={t("enterInArabic")}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                invalid={
                  form.touched.name?.ar && form.errors.name?.ar ? true : false
                }
              />
              {form.touched.name?.ar && form.errors.name?.ar ? (
                <FormFeedback type="invalid">
                  {form.errors.name?.ar as string}
                </FormFeedback>
              ) : null}
            </div>
          </ModalBody>
          <ModalFooter>
            <Link
              to={"#"}
              onClick={onClose}
              className="btn btn-link link-success fw-medium"
            >
              <i className="ri-close-line me-1 align-middle"></i> {t("close")}
            </Link>
            <ActivityButton
              title="save"
              inProgressTitle="saving"
              color="success"
              className="btn btn-success"
              type="submit"
              inProgress={isLoadingAdd || isLoadingUpdate}
              disabled={
                isLoadingAdd ||
                isLoadingUpdate ||
                !!errorSubjectNote ||
                isLoadingUpload
              }
            ></ActivityButton>
          </ModalFooter>
        </Form>
      </Modal>
    </React.Fragment>
  );
};
